<template>
  <section
    class="order-receipt"
    :class="{ 'shop-receipt': summary }"
    aria-label="Itemized order receipt"
  >
    <header class="d-flex justify-space-between align-center ga-3 mb-3">
      <h3
        :class="summary ? 'text-h3 my-2' : 'text-subtitle-1 font-weight-bold'"
      >
        {{ title ?? (summary ? "Summary" : "Order summary") }}
      </h3>
      <span v-if="!summary" class="text-caption text-medium-emphasis"
        >#{{ order.assignId }}</span
      >
    </header>
    <div class="receipt-lines">
      <p v-if="!lines.length" class="text-medium-emphasis py-3">
        Your cart is empty.
      </p>
      <article
        v-for="{ line, depth } in orderedLines"
        :key="line.assignId"
        class="receipt-line"
        :class="summary ? 'py-1' : 'py-3'"
        :style="{ paddingInlineStart: `${depth * 1.5}rem` }"
        :data-line-id="line.assignId"
      >
        <div
          class="d-flex justify-space-between align-center"
          :class="summary ? 'ga-2' : 'ga-3'"
        >
          <div class="receipt-item">
            <div
              class="d-flex flex-wrap align-center ga-2"
              :class="summary ? 'font-weight-bold' : 'font-weight-medium'"
            >
              <span
                >{{ itemTitle(line)
                }}<template v-if="(line.quantity ?? 1) > 1">
                  ({{ line.quantity }})</template
                ></span
              >
              <slot name="line-configure" :line="line" :depth="depth" />
              <slot
                name="recipient"
                :line="line"
                :depth="depth"
                :label="recipientLabel(line)"
              >
                <v-chip
                  v-if="!depth && recipientLabel(line)"
                  density="compact"
                  class="receipt-recipient"
                  >{{ recipientLabel(line) }}</v-chip
                >
              </slot>
            </div>
          </div>
          <span
            class="receipt-line-price text-no-wrap"
            :class="{ 'font-weight-bold': !summary }"
          >
            {{
              money(
                summary
                  ? (line.amountSubtotalCents ?? line.amountTotalCents)
                  : line.amountTotalCents,
              )
            }}
          </span>
          <slot name="line-actions" :line="line" />
        </div>
        <div class="receipt-line-details">
          <div
            v-if="lineDetails?.[line.assignId]?.balanceNumber"
            class="text-caption"
          >
            Gift card / balance:
            {{ lineDetails[line.assignId].balanceNumber }}
          </div>
          <div
            v-if="lineDetails?.[line.assignId]?.cardNumber"
            class="text-caption"
          >
            Card number: {{ lineDetails[line.assignId].cardNumber }}
          </div>
          <div
            v-if="lineDetails?.[line.assignId]?.donationFund"
            class="text-caption"
          >
            Donation fund: {{ lineDetails[line.assignId].donationFund }}
          </div>
          <div
            v-if="lineDetails?.[line.assignId]?.donationInitiative"
            class="text-caption"
          >
            Initiative: {{ lineDetails[line.assignId].donationInitiative }}
          </div>
          <div
            v-for="(discount, index) in lineDetails?.[line.assignId]
              ?.discounts ?? []"
            :key="`discount-${index}`"
            class="receipt-adjustment text-caption"
          >
            <slot
              v-if="summary"
              name="discount"
              :line="line"
              :discount="discount"
              ><v-chip density="compact" color="primary">{{
                discount.name
              }}</v-chip></slot
            >
            <span v-else>Discount: {{ discount.name }}</span
            ><span class="d-inline-flex align-center"
              >{{
                money(
                  discount.amountCents == null ? null : -discount.amountCents,
                )
              }}<slot name="discount-actions" :line="line" :discount="discount"
            /></span>
          </div>
          <div
            v-for="(tax, index) in lineDetails?.[line.assignId]?.taxes ?? []"
            :key="`tax-${index}`"
            class="receipt-adjustment text-caption"
          >
            <span>Tax: {{ tax.name }}</span
            ><span>{{ money(tax.amountCents) }}</span>
          </div>
          <div
            v-if="
              !summary &&
              line.amountDiscountsTotalCents &&
              !lineDetails?.[line.assignId]?.discounts.length
            "
            class="text-caption"
          >
            Discounts: -{{ money(line.amountDiscountsTotalCents) }}
          </div>
          <div
            v-if="
              !summary &&
              line.amountTaxesTotalCents &&
              !lineDetails?.[line.assignId]?.taxes.length
            "
            class="text-caption"
          >
            Tax: {{ money(line.amountTaxesTotalCents) }}
          </div>
          <div v-if="!summary && line.amountRefundedCents" class="text-caption">
            {{ refundedLabel(line) }}: -{{ money(line.amountRefundedCents) }}
          </div>
        </div>
        <template v-if="summary">
          <div
            v-if="
              line.amountTaxesTotalCents &&
              !lineDetails?.[line.assignId]?.taxes.length
            "
            class="receipt-adjustment text-caption"
          >
            <v-chip density="compact" color="yellow">Tax</v-chip>
            <span>{{ money(line.amountTaxesTotalCents) }}</span>
          </div>
          <div
            v-if="
              line.amountDiscountsTotalCents &&
              !lineDetails?.[line.assignId]?.discounts.length
            "
            class="receipt-adjustment text-caption"
          >
            <v-chip density="compact" color="primary">Discounts</v-chip>
            <span>-{{ money(line.amountDiscountsTotalCents) }}</span>
          </div>
          <div
            v-if="line.amountRefundedCents"
            class="receipt-adjustment text-caption"
          >
            <v-chip density="compact" color="error">{{
              refundedLabel(line)
            }}</v-chip>
            <span>-{{ money(line.amountRefundedCents) }}</span>
          </div>
        </template>
      </article>
    </div>
    <div v-if="$slots.payments" class="receipt-payments">
      <slot name="payments" />
    </div>
    <dl v-if="summary" class="receipt-totals pt-2">
      <div v-if="order.amountDiscountsCents" class="text-caption">
        <dt>Total Discounts</dt>
        <dd>-{{ money(order.amountDiscountsCents) }}</dd>
      </div>
      <div class="text-caption">
        <dt>Total Taxes</dt>
        <dd>{{ money(order.amountTaxesCents ?? 0) }}</dd>
      </div>
      <div class="receipt-applied">
        <dt>Payments Applied:</dt>
        <dd>
          {{ money((order.amountPaidCents ?? 0) + authorizedCents) }}
        </dd>
      </div>
      <div v-if="authorizedCents" class="text-caption text-medium-emphasis">
        <dt>Authorized, awaiting capture</dt>
        <dd>{{ money(authorizedCents) }}</dd>
      </div>
      <div v-if="order.amountRefundedCents" class="text-caption">
        <dt>Refunded</dt>
        <dd>-{{ money(order.amountRefundedCents) }}</dd>
      </div>
      <div class="receipt-total">
        <dt>Balance Due:</dt>
        <dd>
          {{
            money(
              order.amountTotalCents == null
                ? null
                : order.amountTotalCents -
                    (order.amountPaidCents ?? 0) -
                    authorizedCents,
            )
          }}
        </dd>
      </div>
    </dl>
    <dl v-else class="receipt-totals pt-3">
      <div v-if="order.amountSubtotalCents != null">
        <dt>Subtotal</dt>
        <dd>{{ money(order.amountSubtotalCents) }}</dd>
      </div>
      <div v-if="order.amountDiscountsCents">
        <dt>Discounts</dt>
        <dd>-{{ money(order.amountDiscountsCents) }}</dd>
      </div>
      <div v-if="order.amountTaxesCents">
        <dt>Tax</dt>
        <dd>{{ money(order.amountTaxesCents) }}</dd>
      </div>
      <div class="receipt-total">
        <dt>Total</dt>
        <dd>{{ money(order.amountTotalCents) }}</dd>
      </div>
      <div v-if="order.amountPaidCents">
        <dt>Settled payments</dt>
        <dd>{{ money(order.amountPaidCents) }}</dd>
      </div>
      <div v-if="order.amountRefundedCents">
        <dt>Refunded</dt>
        <dd>-{{ money(order.amountRefundedCents) }}</dd>
      </div>
      <div v-if="authorizedCents">
        <dt>Authorized, awaiting capture</dt>
        <dd>{{ money(authorizedCents) }}</dd>
      </div>
      <div
        v-if="order.amountTotalCents != null && order.amountPaidCents != null"
      >
        <dt>
          {{
            authorizedCents ? "Remaining after authorization" : "Balance due"
          }}
        </dt>
        <dd>
          {{
            money(
              order.amountTotalCents - order.amountPaidCents - authorizedCents,
            )
          }}
        </dd>
      </div>
    </dl>
    <OrderPaymentDetails
      v-if="payments"
      class="mt-5"
      :payments="payments"
      :member="purchaserIsViewer"
    />
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { OrderMember } from "../../../cbi-generator/generated/dto/OrderMember";
import type { LineItemMember } from "../../../cbi-generator/generated/dto/LineItemMember";
import type { OrderPaymentStaff } from "../../../cbi-generator/generated/dto/OrderPaymentStaff";
import type { PaymentAttempt } from "../../../cbi-generator/generated/dto/PaymentAttempt";
import OrderPaymentDetails from "./OrderPaymentDetails.vue";
import type { ReceiptLineDetails } from "@/composables/shop/receiptLineDetails";

export type ReceiptLine = Pick<
  LineItemMember,
  | "assignId"
  | "quantity"
  | "buyerName"
  | "amountTotalCents"
  | "amountDiscountsTotalCents"
  | "amountTaxesTotalCents"
  | "amountRefundedCents"
  | "itemInstanceIdTargetIds"
> & {
  readonly parentItemId?: { readonly assignId: number } | null;
  readonly amountSubtotalCents?: number | null;
  readonly buyerIsPurchaser?: boolean | null;
  readonly quantityRefunded?: number | null;
};
type ReceiptOrder = Pick<
  OrderMember,
  | "assignId"
  | "amountSubtotalCents"
  | "amountDiscountsCents"
  | "amountTaxesCents"
  | "amountTotalCents"
  | "amountPaidCents"
  | "amountRefundedCents"
>;
const props = withDefaults(
  defineProps<{
    order: ReceiptOrder;
    lines: readonly ReceiptLine[];
    itemTitle: (line: ReceiptLine) => string;
    title?: string;
    summary?: boolean;
    lineDetails?: Readonly<Record<number, ReceiptLineDetails>>;
    authorizedCents?: number;
    payments?: readonly (OrderPaymentStaff | PaymentAttempt)[];
    /** Label unnamed lines bought for the order's own purchaser as "Me". */
    purchaserIsViewer?: boolean;
  }>(),
  { authorizedCents: 0 },
);
const refundedLabel = (line: ReceiptLine) =>
  line.quantityRefunded ? `Refunded (${line.quantityRefunded})` : "Refunded";
function recipientLabel(line: ReceiptLine) {
  const name = line.buyerName?.trim();
  if (name) return name;
  return props.purchaserIsViewer && line.buyerIsPurchaser ? "Me" : null;
}
const orderedLines = computed(() => {
  const ids = new Set(props.lines.map((line) => line.assignId));
  const children = new Map<number, ReceiptLine[]>();
  const roots: ReceiptLine[] = [];
  for (const line of props.lines) {
    const parent = line.parentItemId?.assignId;
    if (parent != null && ids.has(parent))
      children.set(parent, [...(children.get(parent) ?? []), line]);
    else roots.push(line);
  }
  const result: { line: ReceiptLine; depth: number }[] = [];
  const visited = new Set<number>();
  function visit(line: ReceiptLine, depth: number) {
    if (visited.has(line.assignId)) return;
    visited.add(line.assignId);
    result.push({ line, depth });
    for (const child of children.get(line.assignId) ?? [])
      visit(child, depth + 1);
  }
  for (const line of roots) visit(line, 0);
  // Keep historical malformed cycles visible without recursion or losing items.
  for (const line of props.lines) visit(line, 0);
  return result;
});
const money = (value: number | null) =>
  value == null
    ? "--"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value / 100);
</script>

<style scoped>
.receipt-line {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.receipt-item {
  min-width: 0;
  overflow-wrap: anywhere;
  flex: 1;
}
.receipt-totals > div {
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}
.receipt-totals dt {
  margin: 0;
  width: auto;
}
.receipt-totals dd {
  margin: 0;
  white-space: nowrap;
}
.receipt-total {
  font-size: 1.125rem;
  font-weight: 700;
  padding: 0.5rem 0;
}
.shop-receipt {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.shop-receipt header,
.shop-receipt .receipt-totals {
  flex: 0 0 auto;
}
.shop-receipt .receipt-lines {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.shop-receipt .receipt-line {
  border-bottom: 0;
}
.shop-receipt .receipt-line + .receipt-line {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.receipt-adjustment {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.receipt-adjustment > :last-child {
  white-space: nowrap;
  flex-shrink: 0;
}
.receipt-line-details {
  min-width: 0;
  overflow-wrap: anywhere;
}
.receipt-payments {
  flex: 0 1 auto;
  min-height: 0;
  max-height: 35%;
  overflow-y: auto;
}
.shop-receipt .receipt-totals > div {
  margin-bottom: 0.25rem;
}
.shop-receipt .receipt-applied,
.shop-receipt .receipt-total {
  color: rgb(var(--v-theme-warning));
  font-size: 1rem;
  font-weight: 700;
  padding: 0;
}
</style>
